import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { Helmet } from 'react-helmet-async';

import { toast } from '@src/components/core/toaster';
import { useAuth } from '@src/context';
import {
  listEmailNotifications,
  createEmailNotification,
  updateEmailNotification,
  deleteEmailNotification,
} from '@src/api/actions/emailNotifications';

export default function FiveStarEmailNotificationsPage() {
  const { state: authState } = useAuth();
  const memberships = authState?.user?.client_memberships || [];
  const defaultMembership = memberships[0];
  const clientSlug = defaultMembership?.client_slug || defaultMembership?.client_id || '';

  const [emailConfigs, setEmailConfigs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load email notifications for this client
  useEffect(() => {
    if (!clientSlug) { setLoading(false); return; }
    setLoading(true);
    listEmailNotifications(clientSlug)
      .then((res) => {
        setEmailConfigs(res?.data?.results || []);
      })
      .catch(() => setEmailConfigs([]))
      .finally(() => setLoading(false));
  }, [clientSlug]);

  const handleCreate = async () => {
    const res = await createEmailNotification(clientSlug, {
      name: '',
      email: '',
      notify_standard_leads: true,
      notify_self_book_leads: true,
      conditions: [],
    });
    if (res?.data) {
      setEmailConfigs((prev) => [...prev, res.data]);
      toast.success('Recipient added');
    }
  };

  const handleSave = async (ec) => {
    await updateEmailNotification(clientSlug, ec.id, {
      name: ec.name,
      email: ec.email,
      notify_standard_leads: true,
      notify_self_book_leads: true,
      conditions: ec.conditions,
    });
    toast.success('Notification saved');
  };

  const handleDelete = async (id) => {
    await deleteEmailNotification(clientSlug, id);
    setEmailConfigs((prev) => prev.filter((c) => c.id !== id));
    toast.success('Notification removed');
  };

  return (
    <>
      <Helmet><title>Email Notifications</title></Helmet>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Email Notifications
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Configure who receives email notifications when leads come in. Add conditions to route notifications to specific people based on lead data.
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {emailConfigs.map((ec) => (
              <Card key={ec.id} variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <TextField
                        label="Name" size="small" value={ec.name}
                        onChange={(e) => setEmailConfigs((prev) => prev.map((c) => c.id === ec.id ? { ...c, name: e.target.value } : c))}
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        label="Email" size="small" type="email" value={ec.email}
                        onChange={(e) => setEmailConfigs((prev) => prev.map((c) => c.id === ec.id ? { ...c, email: e.target.value } : c))}
                        sx={{ flex: 1 }}
                      />
                      <IconButton size="small" color="error" onClick={() => handleDelete(ec.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>

                    {/* Conditions */}
                    {(ec.conditions || []).length > 0 && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                          Only notify when:
                        </Typography>
                        {ec.conditions.map((cond, ci) => (
                          <Stack key={ci} direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                            <TextField
                              select size="small" value={cond.field || ''}
                              onChange={(e) => {
                                const updated = [...(ec.conditions || [])];
                                updated[ci] = { ...updated[ci], field: e.target.value };
                                setEmailConfigs((prev) => prev.map((c) => c.id === ec.id ? { ...c, conditions: updated } : c));
                              }}
                              sx={{ minWidth: 130 }}
                            >
                              <MenuItem value="territory">Territory</MenuItem>
                              <MenuItem value="vertical">Vertical</MenuItem>
                              <MenuItem value="project_type">Project Type</MenuItem>
                            </TextField>
                            <Typography variant="body2">=</Typography>
                            <TextField
                              size="small" value={cond.value || ''} placeholder="Value"
                              onChange={(e) => {
                                const updated = [...(ec.conditions || [])];
                                updated[ci] = { ...updated[ci], value: e.target.value };
                                setEmailConfigs((prev) => prev.map((c) => c.id === ec.id ? { ...c, conditions: updated } : c));
                              }}
                              sx={{ flex: 1 }}
                            />
                            <IconButton size="small" onClick={() => {
                              const updated = (ec.conditions || []).filter((_, i) => i !== ci);
                              setEmailConfigs((prev) => prev.map((c) => c.id === ec.id ? { ...c, conditions: updated } : c));
                            }}>
                              <DeleteIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Stack>
                        ))}
                      </Box>
                    )}

                    <Stack direction="row" spacing={1}>
                      <Button size="small" startIcon={<AddIcon />} sx={{ textTransform: 'none' }}
                        onClick={() => {
                          const updated = [...(ec.conditions || []), { field: 'territory', value: '' }];
                          setEmailConfigs((prev) => prev.map((c) => c.id === ec.id ? { ...c, conditions: updated } : c));
                        }}>
                        Add condition
                      </Button>
                      <Button size="small" variant="outlined" onClick={() => handleSave(ec)}>
                        Save
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))}

            <Button
              variant="outlined" startIcon={<AddIcon />}
              onClick={handleCreate}
              sx={{ textTransform: 'none' }}
              disabled={!clientSlug}
            >
              Add Email Recipient
            </Button>
          </>
        )}
      </Container>
    </>
  );
}
